import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Progress,
  Select,
  Loader,
  Center,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/hooks/useAuth";
import { apiService } from "@/services/api.service";
import { envConfig } from "@/config/env.config";
import { QuestionGroupList } from "@/components/QuestionGroupList";
import { ReviewStep } from "@/components/ReviewStep";
import {
  BuilderQuestionGroup,
  Company,
  WizardState,
} from "@/types/interview.types";

// Default question groups matching the original intake form
const getDefaultGroups = (): BuilderQuestionGroup[] => [
  {
    id: crypto.randomUUID(),
    name: "Role & Growth",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "number-scale",
        text: "On a scale from 1-5, how well did your roles and responsibilities match your expectation of your role?",
        maxFollowups: 1,
      },
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "What aspects of your role, if any, felt misaligned with your position?",
        maxFollowups: 1,
      },
    ],
  },
  {
    id: crypto.randomUUID(),
    name: "Manager & Team",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "number-scale",
        text: "On a scale from 1-5, how was your relationship with your team and your manager?",
        maxFollowups: 1,
      },
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "Were there specific experiences that built or weakened your trust in your team or manager?",
        maxFollowups: 1,
      },
    ],
  },
  {
    id: crypto.randomUUID(),
    name: "Culture & Leadership",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "number-scale",
        text: "On a scale from 1-5, how would you rate company culture and leadership?",
        maxFollowups: 1,
      },
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "In your last few weeks on the job, what were some ways you felt connected with or disconnected from company values and leadership?",
        maxFollowups: 1,
      },
    ],
  },
  {
    id: crypto.randomUUID(),
    name: "Compensation & Recognition",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "number-scale",
        text: "On a scale from 1-5, how satisfied are you with the compensation and recognition you received in this role?",
        maxFollowups: 1,
      },
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "What were some specific areas where compensation and recognition differed from your expectation?",
        maxFollowups: 1,
      },
    ],
  },
  {
    id: crypto.randomUUID(),
    name: "Workload & Wellbeing",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "number-scale",
        text: "On a scale from 1-5, how would you rate your workload and wellbeing in this role?",
        maxFollowups: 1,
      },
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "Were there any aspects of your job that felt excessively stressful or time consuming?",
        maxFollowups: 1,
      },
    ],
  },
  {
    id: crypto.randomUUID(),
    name: "Additional Feedback",
    questions: [
      {
        id: crypto.randomUUID(),
        groupId: "",
        type: "open-ended",
        text: "Is there anything else you want to share about your experience in this role?",
        maxFollowups: 1,
      },
    ],
  },
];

// Assign groupIds to questions
const initializeGroups = (): BuilderQuestionGroup[] => {
  const groups = getDefaultGroups();
  groups.forEach((group) => {
    group.questions.forEach((q) => {
      q.groupId = group.id;
    });
  });
  return groups;
};

export default function Builder() {
  const { logout } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdLocationId, setCreatedLocationId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const [state, setState] = useState<WizardState>({
    currentStep: 1,
    totalSteps: 2,
    selectedCompanyId: null,
    questionGroups: initializeGroups(),
    openingStatement: "",
    closingStatement: "",
  });

  // Load companies on mount
  useEffect(() => {
    let isMounted = true;

    const loadCompanies = async () => {
      try {
        const data = await apiService.getCompanies();
        if (isMounted) {
          setCompanies(data);
        }
      } catch (err) {
        console.error("Failed to load companies:", err);
        if (isMounted) {
          notifications.show({
            title: "Error",
            message: "Failed to load companies. Please refresh the page.",
            color: "red",
          });
        }
      } finally {
        if (isMounted) {
          setLoadingCompanies(false);
        }
      }
    };

    loadCompanies();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCompanyChange = useCallback((value: string | null) => {
    setState((prev) => ({ ...prev, selectedCompanyId: value }));
  }, []);

  const handleGroupsChange = useCallback((groups: BuilderQuestionGroup[]) => {
    setState((prev) => ({ ...prev, questionGroups: groups }));
  }, []);

  const handleOpeningStatementChange = useCallback((value: string) => {
    setState((prev) => ({ ...prev, openingStatement: value }));
  }, []);

  const handleClosingStatementChange = useCallback((value: string) => {
    setState((prev) => ({ ...prev, closingStatement: value }));
  }, []);

  const validateStep1 = (): boolean => {
    if (!state.selectedCompanyId) {
      notifications.show({
        title: "Company Required",
        message: "Please select a company before continuing.",
        color: "red",
      });
      return false;
    }

    if (state.questionGroups.length === 0) {
      notifications.show({
        title: "Groups Required",
        message: "Please add at least one question group.",
        color: "red",
      });
      return false;
    }

    for (const group of state.questionGroups) {
      if (!group.name.trim()) {
        notifications.show({
          title: "Group Name Required",
          message: "Please provide a name for all question groups.",
          color: "red",
        });
        return false;
      }

      if (group.questions.length === 0) {
        notifications.show({
          title: "Questions Required",
          message: `Please add at least one question to "${group.name}".`,
          color: "red",
        });
        return false;
      }

      for (const q of group.questions) {
        if (!q.text.trim()) {
          notifications.show({
            title: "Question Text Required",
            message: `Please provide text for all questions in "${group.name}".`,
            color: "red",
          });
          return false;
        }
      }
    }

    return true;
  };

  const handleNextStep = () => {
    if (state.currentStep === 1 && !validateStep1()) {
      return;
    }
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.totalSteps, prev.currentStep + 1),
    }));
  };

  const handlePrevStep = () => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1),
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep1()) {
      setState((prev) => ({ ...prev, currentStep: 1 }));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const requestPayload = {
        companyId: state.selectedCompanyId!,
        openingStatement: state.openingStatement || undefined,
        closingStatement: state.closingStatement || undefined,
        questionGroups: state.questionGroups.map((g) => ({
          name: g.name,
          questions: g.questions.map((q) => ({
            text: q.text,
            type: q.type,
            maxFollowups: q.maxFollowups,
          })),
        })),
      };

      const response = await apiService.createInterview(requestPayload);

      notifications.show({
        title: "Success!",
        message: "Interview created successfully!",
        color: "green",
        autoClose: false,
      });

      setCreatedLocationId(response.locationId);
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to create interview:", err);
      setError("Failed to create interview. Please try again.");
      notifications.show({
        title: "Error",
        message: "Failed to create interview. Please try again.",
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setState({
      currentStep: 1,
      totalSteps: 2,
      selectedCompanyId: null,
      questionGroups: initializeGroups(),
      openingStatement: "",
      closingStatement: "",
    });
    setSubmitted(false);
    setCreatedLocationId(null);
    setError(null);
  };

  const progressPercent = (state.currentStep / state.totalSteps) * 100;

  const interviewUrl = createdLocationId
    ? `${envConfig.interviewerUrl}/interview/${createdLocationId}`
    : null;

  if (submitted) {
    return (
      <div className="wizard-container">
        <Container size="md" py="xl">
          <Paper className="success-message" p="xl" radius="md">
            <Stack align="center" gap="md">
              <Title order={2}>Success!</Title>
              <Text>
                Your exit interview setup has been submitted successfully. Your
                Exit Interviewer will be ready to start collecting responses
                within 24 hours.
              </Text>
              {interviewUrl && (
                <Paper withBorder p="md" radius="md" style={{ width: "100%" }}>
                  <Text size="sm" fw={500} mb="xs">
                    Interview Link:
                  </Text>
                  <Text
                    component="a"
                    href={interviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    c="blue"
                    style={{ wordBreak: "break-all" }}
                  >
                    {interviewUrl}
                  </Text>
                </Paper>
              )}
              <Button onClick={handleReset} mt="md">
                Create Another Interview
              </Button>
            </Stack>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <Container size="md" py="xl">
        <div className="wizard-header">
          <Group justify="space-between" mb="md">
            <Title order={1}>Exit Interviewer Setup</Title>
            <Button variant="subtle" color="gray" onClick={logout}>
              Logout
            </Button>
          </Group>

          <div className="wizard-progress">
            <Progress value={progressPercent} size="sm" radius="xl" mb="xs" />
            <Text size="sm" c="dimmed" ta="center">
              Step {state.currentStep} of {state.totalSteps}
            </Text>
          </div>
        </div>

        <Paper className="wizard-form" p="xl" radius="md" withBorder>
          {loadingCompanies ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : (
            <>
              {/* Company Selector - Always visible */}
              <Select
                label="Company"
                placeholder="Select a company"
                data={companies.map((c) => ({ value: c.id, label: c.name }))}
                value={state.selectedCompanyId}
                onChange={handleCompanyChange}
                searchable
                mb="xl"
                required
              />

              {/* Step 1: Create Questions */}
              {state.currentStep === 1 && (
                <div className="wizard-step">
                  <Title order={2} mb="xs">
                    Create questions and groups
                  </Title>
                  <Text className="step-description" c="dimmed" mb="xl">
                    Create question groups that represent the areas where you
                    want to measure employee satisfaction. Add at least one
                    question to each group. Questions can be either open-ended
                    text questions or ratings on a number scale. We've started
                    by recommending some common groups.
                  </Text>

                  <Textarea
                    label="Opening Statement"
                    description="Custom message shown at the beginning of the interview. Leave blank to use the default."
                    placeholder="Welcome to this exit interview. Your feedback is valuable and will help us improve..."
                    value={state.openingStatement}
                    onChange={(e) =>
                      handleOpeningStatementChange(e.target.value)
                    }
                    minRows={3}
                    mb="md"
                  />

                  <Textarea
                    label="Closing Statement"
                    description="Custom message shown at the end of the interview. Leave blank to use the default."
                    placeholder="Thank you for taking the time to share your feedback..."
                    value={state.closingStatement}
                    onChange={(e) =>
                      handleClosingStatementChange(e.target.value)
                    }
                    minRows={3}
                    mb="xl"
                  />

                  <QuestionGroupList
                    groups={state.questionGroups}
                    onChange={handleGroupsChange}
                  />

                  <div className="wizard-actions">
                    <div />
                    <Button onClick={handleNextStep}>
                      Next: Review & submit
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Review & Submit */}
              {state.currentStep === 2 && (
                <div className="wizard-step">
                  <Title order={2} mb="xs">
                    Review & submit
                  </Title>
                  <Text className="step-description" c="dimmed" mb="xl">
                    Review your exit interviewer setup before submitting.
                  </Text>

                  <ReviewStep
                    companyName={
                      companies.find((c) => c.id === state.selectedCompanyId)
                        ?.name || "Unknown Company"
                    }
                    groups={state.questionGroups}
                    openingStatement={state.openingStatement}
                    closingStatement={state.closingStatement}
                  />

                  {error && (
                    <Paper className="error-message" p="md" radius="md" mb="md">
                      <Text c="red">{error}</Text>
                    </Paper>
                  )}

                  <div className="wizard-actions">
                    <Button variant="outline" onClick={handlePrevStep}>
                      Back
                    </Button>
                    <Button onClick={handleSubmit} loading={submitting}>
                      Submit exit interviewer setup
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
}
